/**
 * Grand Quack Auto — audio mixer (master gain + music sub-mix).
 *
 * All Web Audio nodes connect here instead of ctx.destination.
 * M key toggles master mute; gain nodes zero out instantly.
 * HTML5 Audio elements (quack.mp3) use getHtmlVolume() to
 * respect the mute state.
 */

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let musicGain: GainNode | null = null;
let muted = false;

/** Shared AudioContext singleton. Lazily created on first call. */
export function getCtx(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  return ctx;
}

function ensureNodes(): void {
  const c = getCtx();
  if (!masterGain) {
    masterGain = c.createGain();
    masterGain.connect(c.destination);
  }
  if (!musicGain) {
    musicGain = c.createGain();
    musicGain.connect(masterGain);
  }
}

/** SFX destination — all non-music sounds connect here. */
export function getDestination(): GainNode {
  ensureNodes();
  return masterGain!;
}

/** Music destination — background music connects here. */
export function getMusicDestination(): GainNode {
  ensureNodes();
  return musicGain!;
}

/** Toggle master mute on/off. Returns new muted state. */
export function toggleMute(): boolean {
  ensureNodes();
  muted = !muted;
  masterGain!.gain.value = muted ? 0 : 1;
  return muted;
}

export function isMuted(): boolean { return muted; }

/** Scale an HTML5 Audio volume by master mute state. */
export function getHtmlVolume(base: number): number {
  return muted ? 0 : base;
}
